package fr.frux.core.common.crash

/**
 * Règles de nettoyage appliquées à tout ce qui part vers le crash reporter (ADR 0018, #482).
 *
 * Pures et sans SDK, pour être testées hors navigateur. Les requêtes de l'app portent des
 * données personnelles dans leur query string : position (`lat`/`lon` de la carte), texte tapé
 * dans la recherche de lieu (jamais journalisé, ADR 0024), jeton des liens de désinscription
 * (`?token=`). La règle est donc large : aucune query string ni fragment ne sort, quelle que soit
 * la clé.
 */
object CrashReportScrubber {
    private val URL_WITH_QUERY = Regex("""(https?://[^\s?#"'<>]*)[?#][^\s"'<>]*""")
    private val EMAIL = Regex("""[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}""")

    /**
     * Catégorie des breadcrumbs posés par `Logger.e` (#554). Une erreur gérée n'est plus un
     * événement : elle reste dans la piste du prochain crash, comme contexte.
     */
    const val LOG_BREADCRUMB_CATEGORY = "frux.log"

    /**
     * Catégories de breadcrumbs conservées. Les journaux console sont écartés (le `Logger` de
     * debug peut y écrire n'importe quoi), comme les clics, sans valeur sur un canvas Compose.
     * Les erreurs du `Logger` entrent par [LOG_BREADCRUMB_CATEGORY], déjà nettoyées.
     */
    private val KEPT_BREADCRUMB_CATEGORIES = setOf("navigation", "fetch", "xhr", LOG_BREADCRUMB_CATEGORY)

    /** Retire query string et fragment d'une URL seule. */
    fun scrubUrl(url: String): String = url.substringBefore('?').substringBefore('#')

    /**
     * Nettoie un texte libre (message d'erreur, message de log) : query string de toute URL qu'il
     * contient, et toute adresse email. Un message d'exception Ktor reprend l'URL de la requête.
     */
    fun scrubText(text: String): String =
        URL_WITH_QUERY
            .replace(text) { it.groupValues[1] }
            .replace(EMAIL, "[email]")

    fun keepsBreadcrumb(category: String?): Boolean = category in KEPT_BREADCRUMB_CATEGORIES

    /**
     * Message du breadcrumb d'un `Logger.e` (#554) : le tag, le message écrit par le code et la
     * seule classe de l'exception. Le message d'exception est écarté, il reprend souvent l'URL de
     * la requête ou une valeur saisie.
     */
    fun logBreadcrumbMessage(
        tag: String,
        message: String,
        throwable: Throwable?,
    ): String {
        val cause = throwable?.let { " (${it::class.simpleName ?: "Throwable"})" }.orEmpty()
        return scrubText("[$tag] $message$cause")
    }
}
